import React from 'react';

import { SimpleTooltip } from '@/components/ui/tooltip';
import { useStore } from '@/store';
import { calculateDuration, formatDateTimeCompact } from '@/utils/date';

interface RelativeTimeAgoProps {
  /** ISO date string. */
  value: string | undefined;
  className?: string;
}

/**
 * Renders an elapsed-time label ("Xd Yh ago" / "Xh Ym ago" / "Xm ago") for a past
 * timestamp, with a hover tooltip showing the absolute date/time in the user's
 * timezone. Used by the PQC lifecycle columns (Ack Received / Follow-up Requested)
 * where "how long ago" is the scannable signal.
 */
export const RelativeTimeAgo: React.FC<RelativeTimeAgoProps> = React.memo(
  ({ value, className }) => {
    const { getTimeZone } = useStore();
    const timeZone = getTimeZone();

    if (!value) {
      return <span className="text-muted-foreground">N/A</span>;
    }

    const { hours, minutes } = calculateDuration(value, undefined, timeZone);

    let label: string;
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      label = `${days}d ${hours % 24}h ago`;
    } else if (hours >= 1) {
      label = `${hours}h ${minutes}m ago`;
    } else {
      label = `${minutes}m ago`;
    }

    return (
      <SimpleTooltip content={formatDateTimeCompact(value, timeZone)}>
        <span className={className}>{label}</span>
      </SimpleTooltip>
    );
  },
);
RelativeTimeAgo.displayName = 'RelativeTimeAgo';
